import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Square avatar cropper. The user picks which part of their image shows by
 * dragging (pan) and zooming; onSave reports { file, crop } where `crop` is the
 * framed square as { x, y, width, height } in source-image pixels. The full
 * image is uploaded and cropped server-side / at display time, so re-cropping
 * an existing avatar (file === null) needs no re-upload.
 *
 * No cropping dependency — plain pointer events + a little math.
 */

const VIEWPORT = 260; // on-screen crop window (px)

/** Smallest scale that makes the image fully cover the viewport ("cover"). */
const coverScale = (w, h) => Math.max(VIEWPORT / w, VIEWPORT / h);

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function AvatarCropModal({ file, src, onCancel, onSave, saving }) {
  const [img, setImg] = useState(null); // HTMLImageElement
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1); // multiplier over coverScale
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // image top-left in viewport
  const drag = useRef(null); // { startX, startY, ox, oy } while panning

  // Decode the source (a picked File, or an existing avatar URL for re-crop)
  // into an <img> and center it. A picked file is read as a data URL (no
  // object-URL revoke lifecycle, so it can't 404 mid-render); an existing
  // avatar loads straight from its URL. Nothing is drawn to a canvas — we only
  // read the natural size and report crop coordinates — so no CORS needed.
  useEffect(() => {
    let alive = true;
    const fail = () =>
      alive && setError('No hemos podido cargar la imagen para recortarla.');

    const load = (url) => {
      const image = new Image();
      image.onload = () => {
        if (!alive) return;
        const base = coverScale(image.naturalWidth, image.naturalHeight);
        const dispW = image.naturalWidth * base;
        const dispH = image.naturalHeight * base;
        setImg(image);
        setZoom(1);
        setOffset({ x: (VIEWPORT - dispW) / 2, y: (VIEWPORT - dispH) / 2 });
      };
      image.onerror = fail;
      image.src = url;
    };

    if (file) {
      const reader = new FileReader();
      reader.onload = () => alive && load(reader.result);
      reader.onerror = fail;
      reader.readAsDataURL(file);
    } else if (src) {
      load(src);
    }
    return () => {
      alive = false;
    };
  }, [file, src]);

  // Keep the image covering the viewport for the current zoom (no empty gaps).
  const clampOffset = useCallback(
    (o, z) => {
      if (!img) return o;
      const s = coverScale(img.naturalWidth, img.naturalHeight) * z;
      const dispW = img.naturalWidth * s;
      const dispH = img.naturalHeight * s;
      return {
        x: clamp(o.x, VIEWPORT - dispW, 0),
        y: clamp(o.y, VIEWPORT - dispH, 0),
      };
    },
    [img]
  );

  const onZoom = (e) => {
    const z = Number(e.target.value);
    setZoom(z);
    setOffset((o) => clampOffset(o, z));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const next = {
      x: drag.current.ox + (e.clientX - drag.current.startX),
      y: drag.current.oy + (e.clientY - drag.current.startY),
    };
    setOffset(clampOffset(next, zoom));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const handleSave = () => {
    if (!img) return;
    const s = coverScale(img.naturalWidth, img.naturalHeight) * zoom;
    // Source square (in image pixels) currently framed by the viewport, clamped
    // to the image bounds so rounding can't push the rect past its edges.
    const x = Math.round(-offset.x / s);
    const y = Math.round(-offset.y / s);
    const side = Math.round(VIEWPORT / s);
    const crop = {
      x,
      y,
      width: Math.min(side, img.naturalWidth - x),
      height: Math.min(side, img.naturalHeight - y),
    };
    onSave({ file, crop });
  };

  const s = img ? coverScale(img.naturalWidth, img.naturalHeight) * zoom : 1;

  return (
    <div className="Modal" role="presentation" onMouseDown={saving ? undefined : onCancel}>
      <div
        className="Modal__card AvatarCrop"
        role="dialog"
        aria-modal="true"
        aria-label="Recortar foto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="Modal__title">Ajusta tu foto</h2>
        <p className="Modal__desc">Arrastra y usa el zoom para elegir qué parte se verá.</p>
        {error && <p className="Modal__error">{error}</p>}

        <div
          className="AvatarCrop__viewport"
          style={{ width: VIEWPORT, height: VIEWPORT }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {img && (
            <img
              src={img.src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                left: offset.x,
                top: offset.y,
                width: img.naturalWidth * s,
                height: img.naturalHeight * s,
                userSelect: 'none',
              }}
            />
          )}
        </div>

        <input
          className="AvatarCrop__zoom"
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          onChange={onZoom}
          aria-label="Zoom"
        />

        <div className="Modal__actions">
          <button type="button" className="Btn Btn--soft" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="Btn Btn--primary" onClick={handleSave} disabled={!img || saving}>
            {saving ? 'Guardando…' : 'Guardar foto'}
          </button>
        </div>
      </div>
    </div>
  );
}
